import React, { useCallback, useRef, useState } from 'react';
import { Input } from '@rebass/forms';
import { Button } from 'rebass';
import { FiPlus, FiX } from 'react-icons/fi';
import classNames from 'classnames';

import Badge from 'components/Badge';

/**
 * Custom hook to handle the Enter key event.
 *
 * @param {function} callback Function to call when Enter is pressed
 * @param {Array} deps Dependencies for the useCallback hook
 */
const useOnEnter = (callback, deps) =>
    useCallback(event => {
        if (event.key === 'Enter' && !event.repeat) {
            event.preventDefault();
            callback(event);
        }
    }, deps);

/**
 * Validates if the given string is a valid email address.
 *
 * @param {string} email Email address to validate
 * @returns {boolean} True if email is valid, false otherwise
 */
const isEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Component to display email tags.
 *
 * @param {string[]} emails Array of emails
 * @param {function} onRemove Callback to remove an email
 * @param {?string} className Custom class name
 */
const EmailTags = ({ emails, onRemove, className }) => {
    if (!Array.isArray(emails)) return null;

    return (
        <div className={classNames(className, 'flex flex-wrap')} style={{ gap: '8px' }}>
            {emails.map(email => (
                <Badge type="main">
                    {email}
                    <Button
                        variant="transparent-icon"
                        type="button"
                        onClick={() => onRemove(email)}
                        style={{ marginLeft: '8px', height: '20px', padding: '2px' }}
                        css={{
                            svg: {
                                margin: '0 !important',
                                opacity: '0.4'
                            }
                        }}
                    >
                        <FiX />
                    </Button>
                </Badge>
            ))}
        </div>
    );
};

/**
 * Component to add CC emails.
 *
 * @param {?string} id Custom id for the input
 * @param {string[]} value Emails
 * @param {function} onChange Callback to update the emails
 * @param {?string} className Custom class name
 * @param {?React.CSSProperties} style Custom styles
 * @param {?string} openClassName Class name when the input is open
 * @param {?string} closeClassName Class name when the input is closed
 * @param {?string} addCcButtonVariant Variant for the "Add CC" button (default: "secondary")
 * @param {?string} addButtonVariant Variant for the "Add" button (default: "primary")
 */
export function EmailCcTokenizer({
    id,
    value,
    onChange,
    className,
    style,
    openClassName,
    closeClassName,
    addCcButtonVariant = 'secondary',
    addButtonVariant = 'primary'
}) {
    /**
     * @type {React.MutableRefObject<HTMLInputElement>}
     */
    const ref = useRef();
    const [isInvalid, setIsInvalid] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const hasEmails = Array.isArray(value) && value.length > 0;

    const clearInput = () => {
        ref.current.value = '';
        setIsInvalid(false);
    };

    const add = () => {
        const email = ref.current.value.trim();

        if (!email || !isEmail(email)) return setIsInvalid(true);

        onChange([...value, email]);
        clearInput();
    };

    const remove = email => {
        const newEmails = value.filter(item => item !== email);
        onChange(newEmails);
    };

    const handleEnter = useOnEnter(() => {
        add();
    }, [value]);

    if (!isActive)
        return (
            <div className={classNames(className, closeClassName)} style={style}>
                <Button
                    type="button"
                    variant={addCcButtonVariant}
                    onClick={() => setIsActive(true)}
                    style={{ marginBottom: hasEmails ? '8px' : 0 }}
                >
                    <FiPlus /> Add CC
                </Button>
                <EmailTags emails={value} onRemove={remove} />
            </div>
        );

    return (
        <div className={classNames(className, openClassName)} style={style}>
            <div className="flex" style={{ gap: '8px', marginBottom: hasEmails ? '8px' : 0, width: '100%' }}>
                <Input
                    id={id}
                    ref={ref}
                    autoFocus
                    flexGrow={1}
                    placeholder="Enter email"
                    onKeyDown={handleEnter}
                    className={classNames(isInvalid && 'shadow-solid-danger--important')}
                />
                <Button
                    type="button"
                    variant={addButtonVariant}
                    style={{ margin: 0, minWidth: 'max-content' }}
                    onClick={add}
                >
                    Add
                </Button>
                <Button style={{ minWidth: '32px' }} variant="transparent-icon" onClick={() => setIsActive(false)}>
                    <FiX />
                </Button>
            </div>
            <EmailTags emails={value} onRemove={remove} />
        </div>
    );
}
